import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

import { getImageUrl } from '../utils/imageUtils';
const Campuses = () => {
  const { t, i18n } = useTranslation();

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('campuses.meta.title')}</title>
        <meta name="description" content={t('campuses.meta.description')} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t('campuses.meta.title')} />
        <meta property="og:description" content={t('campuses.meta.description')} />
        <meta property="og:url" content={`https://tapngrab.ca/${i18n.language === 'fr' ? 'fr/campus' : 'campuses'}`} />
        <meta property="og:image" content="https://tapngrab.ca/images/smart-store.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content={i18n.language === 'fr' ? 'fr_CA' : 'en_CA'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('campuses.meta.title')} />
        <meta name="twitter:description" content={t('campuses.meta.description')} />
        <meta name="twitter:image" content="https://tapngrab.ca/images/smart-store.png" />
        <link rel="canonical" href="https://tapngrab.ca/campuses" />
        <link rel="alternate" hreflang="fr" href="https://tapngrab.ca/fr/campus" />
        <link rel="alternate" hreflang="en" href="https://tapngrab.ca/campuses" />
      </Helmet>

      <div className="text-slate-800 bg-white">
        <Header />
        
        {/* Hero Section */}
        <section className="hero-grad py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-2 items-center">
              <div>
                <h1 className="text-4xl font-bold text-slate-900 sm:text-5xl lg:text-6xl">
                  {t('campuses.hero.title')}
                </h1>
                <p className="mt-6 text-lg text-slate-600">
                  {t('campuses.hero.subtitle')}
                </p>
                <div className="mt-8">
                  <Link 
                    to="/request-placement"
                    className="rounded-2xl px-6 py-3 text-sm font-semibold text-white glow"
                    style={{ background: 'linear-gradient(90deg, #06b6d4, #3b82f6, #6366f1)' }}
                  >
                    {t('campuses.cta.button')}
                  </Link>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={getImageUrl("/images/smart-store.png")} 
                  alt="Three Tap'nGrab smart coolers displaying snacks, beverages, and personal care products"
                  className="w-full h-auto rounded-[2rem] chip"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="bg-white py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-2 items-center">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">
                  {t('campuses.problem.title')}
                </h2>
                <p className="mt-4 text-lg text-slate-600">
                  {t('campuses.problem.description')}
                </p>
              </div>
              <div className="relative">
                <img 
                  src={getImageUrl("/images/smart-pantry-angle.png")} 
                  alt="Sleek black Tap'nGrab smart cooler for modern office and commercial spaces"
                  className="w-full h-auto rounded-[2rem] chip"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="bg-slate-50 py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-2 items-center">
              <div className="order-2 lg:order-1">
                <img 
                  src={getImageUrl("/images/smart-fridge-transparent.png")} 
                  alt="Tap'nGrab cooler stocked with fresh salads, healthy meals, and beverages"
                  className="w-full h-auto rounded-[2rem] chip"
                />
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">
                  {t('campuses.solution.title')}
                </h2>
                <p className="mt-4 text-lg text-slate-600">
                  {t('campuses.solution.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="bg-white py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">
                {t('campuses.benefits.title')}
              </h2>
            </div>
            <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {t('campuses.benefits.list', { returnObjects: true }).map((benefit, index) => (
                <div key={index} className="rounded-2xl border bg-white p-6 chip">
                  <div className="text-2xl mb-3">✓</div>
                  <p className="text-sm text-slate-600">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Requirements Section */}
        <section className="bg-slate-50 py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">
                {t('campuses.requirements.title')}
              </h2>
            </div>
            <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {t('campuses.requirements.list', { returnObjects: true }).map((requirement, index) => (
                <div key={index} className="rounded-2xl border bg-white p-6 chip text-center">
                  <div className="text-2xl mb-3">📋</div>
                  <p className="text-sm text-slate-600">{requirement}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-white py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl border bg-white p-8 chip">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">
                  {t('campuses.cta.title')}
                </h2>
                <div className="mt-8">
                  <Link 
                    to="/request-placement"
                    className="rounded-2xl px-6 py-3 text-sm font-semibold text-white glow"
                    style={{ background: 'linear-gradient(90deg, #06b6d4, #3b82f6, #6366f1)' }}
                  >
                    {t('campuses.cta.button')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Campuses;
